import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, FolderKanban, FileText, Users, Mail, Search,
  BarChart3, Sparkles, Settings, LogOut, Menu, Bell, ChevronsLeft, ChevronsRight, Inbox, Receipt, Bot,
} from "lucide-react";
import CommandPalette from "./CommandPalette";
import NotificationsBell from "./NotificationsBell";
import SmartRoutingButton from "./SmartRoutingButton";

const GOV_ROLES = ["managing_partner", "admin"];
const LEAD_ROLES = ["managing_partner", "admin", "partner"];
const BILLING_ROLES = ["managing_partner", "admin", "amministrazione"];

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/memo", label: "Memo", icon: Inbox, testid: "nav-memo" },
  { to: "/pratiche", label: "Pratiche", icon: FolderKanban, testid: "nav-pratiche" },
  { to: "/documenti", label: "Documenti", icon: FileText, testid: "nav-documenti" },
  { to: "/contatti", label: "Contatti", icon: Users, testid: "nav-contatti" },
  { to: "/fatturazione", label: "Fatturazione", icon: Receipt, testid: "nav-fatturazione" },
  { to: "/email", label: "Email", icon: Mail, testid: "nav-email" },
  { to: "/ricerca", label: "Ricerca", icon: Search, testid: "nav-ricerca" },
  { to: "/report", label: "Report", icon: BarChart3, testid: "nav-report", roles: LEAD_ROLES },
  { to: "/ai", label: "AI Assistant", icon: Sparkles, testid: "nav-ai", roles: LEAD_ROLES },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, testid: "nav-settings", roles: GOV_ROLES },
  { to: "/automazioni", label: "Automazioni", icon: Bot, testid: "nav-automations", roles: GOV_ROLES },
];

function SidebarContent({ collapsed, role, onClick }) {
  return (
    <div className="h-full flex flex-col bg-navy-800 text-white">
      <div className={`flex items-center gap-3 px-4 py-5 border-b border-navy-700 ${collapsed ? "justify-center" : ""}`}>
        <div className="w-9 h-9 rounded-md bg-navy-500 flex items-center justify-center font-heading font-bold flex-shrink-0">ST</div>
        {!collapsed && (
          <div>
            <div className="font-heading font-bold text-sm">Studio Manager</div>
            <div className="text-[10px] text-navy-100">Tozzi e Associati</div>
          </div>
        )}
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navItems.filter(n => !n.roles || n.roles.includes(role)).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/dashboard"}
            onClick={onClick}
            className={({ isActive }) => `sidebar-nav-item ${isActive ? "active" : ""} ${collapsed ? "justify-center" : ""}`}
            data-testid={n.testid}
          >
            <n.icon className="w-4 h-4 flex-shrink-0" />
            {!collapsed && <span>{n.label}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 text-[10px] text-navy-100 border-t border-navy-700 text-center">
        {!collapsed && "v1.0 · Studio Manager"}
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const crumb = (() => {
    const map = {
      "/dashboard": "Dashboard", "/memo": "Memo", "/pratiche": "Pratiche", "/documenti": "Documenti",
      "/contatti": "Contatti", "/fatturazione": "Fatturazione", "/email": "Email", "/ricerca": "Ricerca",
      "/report": "Report", "/ai": "AI Assistant", "/impostazioni": "Impostazioni", "/automazioni": "Automazioni",
    };
    if (map[location.pathname]) return map[location.pathname];
    if (location.pathname.startsWith("/pratiche/")) return "Pratiche · Dettaglio";
    return "";
  })();

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop sidebar */}
      <aside className={`hidden lg:block transition-all duration-300 ${collapsed ? "w-16" : "w-60"}`}>
        <div className="fixed h-screen" style={{ width: collapsed ? "4rem" : "15rem" }}>
          <SidebarContent collapsed={collapsed} role={user?.role} />
        </div>
      </aside>

      {/* Mobile sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-64 bg-navy-800 border-r-0">
          <SidebarContent collapsed={false} role={user?.role} onClick={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 h-14 bg-white border-b border-slate-200 flex items-center px-4 gap-3">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5" />
          </Button>
          <Button variant="ghost" size="icon" className="hidden lg:flex" onClick={() => setCollapsed((v) => !v)} data-testid="sidebar-toggle">
            {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
          </Button>
          <div className="text-sm text-slate-600 hidden sm:block" data-testid="breadcrumb">
            <span className="text-slate-400">Studio Manager</span>
            {crumb && <> <span className="mx-2 text-slate-300">/</span> <span className="text-navy-900 font-medium">{crumb}</span></>}
          </div>
          <div className="flex-1" />
          <button
            onClick={() => setPaletteOpen(true)}
            className="hidden md:flex items-center gap-2 px-3 h-9 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-500 hover:bg-slate-100 transition-colors w-72"
            data-testid="global-search-button"
          >
            <Search className="w-4 h-4" />
            <span>Cerca pratiche, documenti, contatti...</span>
            <kbd className="ml-auto text-[10px] bg-white border border-slate-200 rounded px-1.5 py-0.5">⌘K</kbd>
          </button>
          {user && ["managing_partner", "admin", "partner", "segreteria"].includes(user.role) && <SmartRoutingButton />}
          <NotificationsBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 px-2 h-9 rounded-md hover:bg-slate-100 transition-colors" data-testid="user-menu-btn">
                <Avatar className="w-7 h-7"><AvatarFallback className="bg-navy-800 text-white text-xs">{(user?.full_name || "U").split(" ").map(s=>s[0]).slice(0,2).join("")}</AvatarFallback></Avatar>
                <div className="text-left hidden md:block leading-tight">
                  <div className="text-xs font-medium text-navy-900">{user?.full_name}</div>
                  <div className="text-[10px] text-slate-500 capitalize">{user?.role?.replace("_", " ")}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/impostazioni")} data-testid="menu-profile">Profilo</DropdownMenuItem>
              <DropdownMenuItem onClick={onLogout} className="text-red-600" data-testid="menu-logout">
                <LogOut className="w-4 h-4 mr-2" /> Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <div className="p-4 md:p-6 animate-fade-in">
          <Outlet />
        </div>
      </main>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
